import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Minus, RotateCcw, Save, ShieldCheck } from 'lucide-react';
import {
  CAPABILITY_LABELS,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  capabilitiesFor,
  reachesCapability,
  type Capability,
  type Role,
  type RoleMatrix,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Pill,
  Spinner,
} from '../components/ui';

/**
 * Editable role → capability matrix, per org. Super admin holds everything and
 * is shown for reference; the org roles are customisable within the limits the
 * API enforces (admins edit engineer/operator/viewer/technician and never
 * their own role — `ADMIN_EDITABLE_ROLES` in shared; platform capabilities
 * are never grantable to an org role). The Technician column — last, below
 * the viewer, because a technician sees only the ONUs on jobs assigned to
 * them (`onu.read_assigned`) and none of the fleet — is where a senior tech
 * is widened: tick "View OLTs and drift" for the whole fleet, a reboot, the
 * whole plant — instead of promoting them to operator and handing every CPE
 * write over with it. Placing their own subscribers' ONUs (`onu.place`) is a
 * default of the rung since 2026-09-12, like the assigned read: no rung holds
 * it by name, an admin ticks it because `topology.manage` reaches it.
 *
 * The capability labels come from `@simpleacs/shared` rather than living here:
 * the API-key picker renders the same list, and a second copy would eventually
 * describe the same checkbox two different ways.
 */
export function RolesPage() {
  const { session, activeOrg } = useSession();
  const queryClient = useQueryClient();
  const needsOrg = session?.user.role === 'superadmin' && !activeOrg;

  const matrixQuery = useQuery({
    queryKey: ['roles', activeOrg?.id ?? 'self'],
    queryFn: () => api.get<RoleMatrix>('/roles'),
    enabled: !needsOrg,
  });
  const data = matrixQuery.data;

  // Local editable copy, keyed by role, seeded from the server whenever it loads.
  const [draft, setDraft] = useState<Record<string, Set<Capability>>>({});
  useEffect(() => {
    if (!data) return;
    const next: Record<string, Set<Capability>> = {};
    for (const row of data.rows) next[row.role] = new Set(row.capabilities);
    setDraft(next);
  }, [data]);

  const save = useMutation({
    mutationFn: (input: { role: Role; capabilities: Capability[] }) =>
      api.put(`/roles/${encodeURIComponent(input.role)}`, { capabilities: input.capabilities }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['roles'] }),
  });
  const reset = useMutation({
    mutationFn: (role: Role) => api.delete(`/roles/${encodeURIComponent(role)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['roles'] }),
  });

  const grantable = new Set(data?.grantable ?? []);
  /*
    The reference column says what a super admin REACHES, not which names sit
    on their rung: `onu.read_assigned` and `onu.place` sit on no rung at all
    (they are the technician's grants), and a super admin reaches those slices
    through `olt.read` and `topology.manage`, the wider names they narrow —
    the same rule the editor guard applies when an admin ticks them in the
    Technician column.
  */
  const superHolds = new Set<string>(capabilitiesFor('superadmin'));

  function serverSet(role: Role): Set<Capability> {
    return new Set(data?.rows.find((r) => r.role === role)?.capabilities ?? []);
  }
  function isDirty(role: Role): boolean {
    const d = draft[role];
    if (!d) return false;
    const s = serverSet(role);
    if (d.size !== s.size) return true;
    for (const c of d) if (!s.has(c)) return true;
    return false;
  }
  function toggle(role: Role, capability: Capability) {
    setDraft((prev) => {
      const set = new Set(prev[role]);
      if (set.has(capability)) set.delete(capability);
      else set.add(capability);
      return { ...prev, [role]: set };
    });
  }

  return (
    <>
      <PageHeader
        title="Roles"
        description={
          activeOrg
            ? `What each role can do in ${activeOrg.name}. Assign roles per user on the Users page.`
            : 'What each role can do. Assign roles per user on the Users page.'
        }
      />

      {needsOrg ? (
        <Card>
          <EmptyState
            icon={<ShieldCheck className="size-8" />}
            title="Select an organization"
            description="Roles are customised per organization — pick one in the top bar to edit its roles."
          />
        </Card>
      ) : matrixQuery.isLoading ? (
        <Card>
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        </Card>
      ) : matrixQuery.error ? (
        <Card>
          <div className="p-5">
            <ErrorNotice error={matrixQuery.error} />
          </div>
        </Card>
      ) : data ? (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="border-b border-line bg-surface-2/60">
                <tr>
                  <th className="th">Capability</th>
                  <th className="th text-center" title={ROLE_DESCRIPTIONS.superadmin}>
                    <Pill tone="primary">{ROLE_LABELS.superadmin}</Pill>
                    <div className="mt-1 text-[10px] uppercase tracking-wide text-faint">all</div>
                  </th>
                  {data.rows.map((row) => {
                    const dirty = isDirty(row.role);
                    return (
                      <th
                        key={row.role}
                        className="th text-center align-top"
                        title={ROLE_DESCRIPTIONS[row.role]}
                      >
                        <Pill tone="neutral">{row.label}</Pill>
                        <div className="mt-1 text-[10px] uppercase tracking-wide text-faint">
                          {row.isDefault ? 'default' : 'custom'}
                        </div>
                        {row.canEdit ? (
                          <div className="mt-1.5 flex justify-center gap-1">
                            <Button
                              size="sm"
                              variant={dirty ? 'primary' : 'ghost'}
                              icon={<Save className="size-3" />}
                              disabled={!dirty}
                              loading={save.isPending && save.variables?.role === row.role}
                              onClick={() =>
                                save.mutate({ role: row.role, capabilities: [...(draft[row.role] ?? [])] })
                              }
                            >
                              Save
                            </Button>
                            {!row.isDefault && (
                              <Button
                                size="sm"
                                icon={<RotateCcw className="size-3" />}
                                loading={reset.isPending && reset.variables === row.role}
                                title="Reset to defaults"
                                onClick={() => reset.mutate(row.role)}
                              >
                                Reset
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="mt-1 text-[10px] text-faint">read-only</div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {data.allCapabilities.map((capability) => (
                  <tr key={capability} className="hover:bg-surface-2/70">
                    <td className="td">
                      {CAPABILITY_LABELS[capability]}
                      <span className="ml-2 font-mono text-[11px] text-faint">{capability}</span>
                    </td>
                    <td className="td text-center">
                      {reachesCapability(superHolds, capability) ? (
                        <Check className="mx-auto size-4 text-success" />
                      ) : (
                        <Minus className="mx-auto size-4 text-faint" />
                      )}
                    </td>
                    {data.rows.map((row) => {
                      const grantableHere = grantable.has(capability);
                      const checked = draft[row.role]?.has(capability) ?? false;
                      // Platform capabilities are never grantable to an org role.
                      if (!grantableHere) {
                        return (
                          <td key={row.role} className="td text-center">
                            <Minus className="mx-auto size-4 text-faint/50" />
                          </td>
                        );
                      }
                      if (!row.canEdit) {
                        return (
                          <td key={row.role} className="td text-center">
                            {checked ? (
                              <Check className="mx-auto size-4 text-success" />
                            ) : (
                              <Minus className="mx-auto size-4 text-faint" />
                            )}
                          </td>
                        );
                      }
                      return (
                        <td key={row.role} className="td text-center">
                          <input
                            type="checkbox"
                            className="size-4 cursor-pointer accent-primary"
                            checked={checked}
                            onChange={() => toggle(row.role, capability)}
                            aria-label={`${row.label}: ${CAPABILITY_LABELS[capability]}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-start gap-2 border-t border-line px-5 py-3 text-xs text-muted">
            <ShieldCheck className="mt-0.5 size-4 shrink-0 text-primary" />
            Changes apply only to {data.orgName}. Super admin reaches every capability;
            platform capabilities (greyed) are never grantable to an org role. Tenancy is enforced in
            the API — a role never grants access to another organization's data.
          </div>
        </Card>
      ) : null}
    </>
  );
}
